import { experimental_createQueryPersister } from '@tanstack/query-persist-client-core'
import { QueryCache, QueryClient } from '@tanstack/vue-query'

import { LIVE_PUBLIC_PREFIXES } from '@/liveContent'
import { queryKeys } from '@/queryKeys'
import { supabase } from '@/supabase'

const PERSIST_PREFIX = 'tanstack-query'

// Query data that is scoped to the current authenticated user. A 401/PGRST303
// on one of these means the session is genuinely dead — sign out. Errors on
// public queries (home cards, movies, now, spotify, guestbook) must NOT log
// the user out, since those can fail transiently while a session is fine.
const AUTH_SCOPED_PREFIXES = [
  queryKeys.travel.trips[0],
  queryKeys.travel.tripImages[0],
  queryKeys.travel.tripsWithImages[0],
  'admin', // generic prefix matching every admin-* key
  queryKeys.admin.blog[0],
]

// Queries whose results depend on the signed-in user (clearance-gated trips,
// blog listing + gated markdown, now page). These must never be written to
// localStorage: a persisted cache would let the next visitor see the previous
// user's gated data without re-checking auth.
const SENSITIVE_PREFIXES = [
  ...AUTH_SCOPED_PREFIXES,
  queryKeys.blog.list[0],
  queryKeys.blog.postBase[0],
  queryKeys.blog.contentBase[0],
  queryKeys.quotes[0],
  queryKeys.thwips[0],
]

function isAdminKey(key: string): boolean {
  return key.startsWith('admin')
}

const BUSTER = '1'

const persister = experimental_createQueryPersister({
  filters: {
    predicate: (query) => {
      const key = query.queryKey[0]
      return typeof key !== 'string' || (!SENSITIVE_PREFIXES.includes(key) && !isAdminKey(key))
    },
  },
  maxAge: 1000 * 60 * 60 * 12, // 12 hours
  storage: window.localStorage,
})

// Real live-content tables (blog, trips, trip_images, guestbook, quotes,
// work_people — see @/liveContent) are the only queries that can change while
// someone is looking at the site, and the only actor that ever changes them
// is this site itself via realtime/publication. So for those:
//   - a LONG staleTime: time never makes them stale; if nothing changed, the
//     cache is simply right. Realtime invalidation is the only refresh.
//   - refetchOnRestore: 'always': realtime events don't replay across reloads,
//     so a restored localStorage snapshot ALWAYS re-fetches before painting.
//     This is the load-bearing guarantee: a month-long staleTime must never
//     mean "reload shows a pre-edit blob."
// Bump BUSTER above whenever a query shape changes (new column, renamed key)
// to hard-drop stale persisted snapshots on deploy.
const livePersister = experimental_createQueryPersister({
  buster: BUSTER,
  filters: {
    predicate: (query) => {
      const key = query.queryKey[0]
      return typeof key === 'string' && LIVE_PUBLIC_PREFIXES.includes(key)
    },
  },
  maxAge: 1000 * 60 * 60 * 24 * 30, // 30 days
  refetchOnRestore: 'always',
  storage: window.localStorage,
})

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      persister: persister.persisterFn,
      staleTime: 1000 * 60 * 5, // 5 mins
    },
  },
  queryCache: new QueryCache({
    onError: (error: unknown, query: { queryKey: readonly unknown[] }) => {
      // Only force a sign-out when an auth-scoped query hits an auth error
      if (error && typeof error === 'object') {
        const err = error as { code?: string; message?: string; status?: number }
        const isAuthError =
          err.code === 'PGRST303' || err.message === 'JWT expired' || err.status === 401
        if (isAuthError && isAuthScoped(query.queryKey)) {
          void forceSignOut()
        }
      }
    },
  }),
})

/**
 * Hard sign-out used when an auth-scoped query proves the session is dead.
 * Unlike a bare auth.signOut(), this also wipes the in-memory query cache and
 * the localStorage persistence layer so the previous user's gated data can't
 * resurface after the session ends.
 */
export async function forceSignOut() {
  await supabase.auth.signOut()
  clearPersistedCache()
}

/**
 * Drop all cached query data, including the localStorage persistence layer.
 * Used on sign-out so a different user never sees the previous user's data.
 */
function clearPersistedCache() {
  if (typeof window !== 'undefined' && window.localStorage) {
    const keys = Object.keys(window.localStorage).filter((k) => k.startsWith(PERSIST_PREFIX))
    keys.forEach((k) => window.localStorage.removeItem(k))
  }
  queryClient.clear()
}

function isAuthScoped(key: readonly unknown[]): boolean {
  return (
    typeof key[0] === 'string' &&
    (AUTH_SCOPED_PREFIXES.indexOf(key[0]) !== -1 || isAdminKey(key[0]))
  )
}

// Set immediate fetch / no caching for all admin queries
const adminKeys = Object.values(queryKeys.admin)

adminKeys.forEach((key) => {
  queryClient.setQueryDefaults(key, {
    gcTime: 0,
    persister: undefined,
    staleTime: 0,
  })
})

// Live content: long single staleTime, restore-then-refetch persister.
for (const key of LIVE_PUBLIC_PREFIXES) {
  queryClient.setQueryDefaults([key], {
    persister: livePersister.persisterFn,
    staleTime: 1000 * 60 * 60 * 24 * 30, // 30 days (cache lives until an event says otherwise)
  })
}
